import React, { FC, useMemo } from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { FormControl, FormHelperText, Grid, InputLabel, OutlinedInput } from '@mui/material'
import { capitalize } from 'lodash'
import { NumericFormat } from 'react-number-format'

const useStyles = makeStyles((theme: Theme) => ({
  inputContainer: {
    marginBottom: 20,
    width: '100%',
    '& .MuiOutlinedInput-root': { height: 40 },
    '& fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
    '& .MuiOutlinedInput-root.Mui-disabled': {
      '& fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
      '& .MuiOutlinedInput-input': { WebkitTextFillColor: '#FFFFFF !important' },
    },
  },
  label: {
    fontSize: 18,
    color: '#FFFFFF',
    opacity: '60%',
  },
  formControl: {
    width: '100%',
    height: 40,
    boxSizing: 'border-box',
    '& .MuiInputBase-root': { fontSize: '14px !important' },
    '& .MuiOutlinedInput-input': {
      paddingTop: 20,
      paddingBottom: 20,
    },
    '& .MuiOutlinedInput-input:-webkit-autofill': {
      '-webkit-box-shadow': 'none',
    },
    '& :-webkit-autofill': {
      transition: 'background-color 5000s ease-in-out 0s',
    },
  },
  postfixIcon: {
    fontSize: 14,
    fontWeight: 400,
    paddingRight: 4,
    color: theme.palette.primary.light,
    opacity: 0.5,
  },
}))

interface InputFieldProps {
  labelName: string
  inputName: string
  disable?: boolean
  postfix?: string
  // eslint-disable-next-line
  rules?: any
  placeholder?: string
  thousandSeparator?: boolean
  decimalScale?: number
  fixedDecimalScale?: boolean
  onClick?: () => void
  customValue?: number | string
  onValueTransform?: (value: number | string | undefined) => number | string | undefined
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
}

export const TradingInput: FC<InputFieldProps> = ({
  labelName,
  inputName,
  disable,
  postfix,
  rules,
  placeholder,
  thousandSeparator,
  decimalScale,
  fixedDecimalScale,
  onClick,
  customValue,
  onValueTransform,
  onFocus,
  onBlur,
}) => {
  const { label, inputContainer, formControl, postfixIcon } = useStyles()

  const {
    control,
    formState: { errors },
  } = useFormContext()
  const error = errors[inputName]
  const errorMessage = useMemo(() => {
    if (!error) return ''
    const message = error.message || ''
    if (message.includes('between') || message.includes('decimal places')) return message
    return capitalize(message.replace(inputName, labelName))
  }, [error, inputName, labelName])

  return (
    <Grid container item className={inputContainer} direction="column" wrap="nowrap">
      <Grid item>
        <InputLabel shrink htmlFor={inputName} className={label}>
          {labelName}
        </InputLabel>
      </Grid>
      <Grid item>
        <FormControl size="small" className={formControl}>
          <Controller
            control={control}
            name={inputName}
            rules={rules}
            render={({ field: { onChange, onBlur: onBlurRenderProp, value, ref, name } }) => (
              <NumericFormat
                customInput={OutlinedInput}
                name={name}
                value={customValue !== undefined ? customValue : value ?? ''}
                valueIsNumericString
                onValueChange={({ value: rawValue }) => {
                  const next = onValueTransform ? onValueTransform(rawValue) : rawValue
                  onChange(next)
                }}
                thousandSeparator={thousandSeparator}
                decimalScale={decimalScale}
                fixedDecimalScale={fixedDecimalScale}
                allowNegative={false}
                placeholder={placeholder}
                error={!!error}
                inputRef={ref}
                id={inputName}
                color="primary"
                disabled={disable}
                endAdornment={postfix && <span className={postfixIcon}>{postfix}</span>}
                onClick={onClick}
                onFocus={(e) => {
                  onFocus?.(e)
                }}
                onBlur={(e) => {
                  onBlurRenderProp?.()
                  onBlur?.(e)
                }}
              />
            )}
          />
        </FormControl>
      </Grid>
      <Grid item>
        {error && <FormHelperText id={`component-error-text-${inputName}`}>{errorMessage}</FormHelperText>}
      </Grid>
    </Grid>
  )
}
